
import { setup } from 'vue-class-component';
import { Options, Vue } from 'vue-property-decorator';
import AxiosMockAdapter from 'axios-mock-adapter';
import remoteModuleImportMap from '@/config/remote-module-imports';
import { useAppStore, useDeveloperSettingsStore } from '@/store';
import i18n from '@/i18n';

// Components
import MhtkModuleInjector from '@/components/global-control/mhtk-module-injector.vue';

// Composition
import { useTeleportComponentList } from '@/composables/mount-component';
import { AppManagementService, createAppManagementService } from '@/composables/service';

// API Services
import { useAuthenticationService } from 'commercial-web-client-utilities/dist/composables/authentication-service';
import { useLedsService } from 'commercial-web-client-utilities/dist/composables/leds-service';
import { useCommwebService } from 'commercial-web-client-utilities/dist/composables/commweb-service';
import { useNonsecureService } from 'commercial-web-client-utilities/dist/composables/nonsecure-service';

// Types
import type { I18n } from 'vue-i18n';
import type {
    SiteConfiguration,
    PluginPackageInitializeModuleOptions,
} from '@/types';

interface MhtkModuleInitialization {
    moduleName: string;
    initializeModule: (options: PluginPackageInitializeModuleOptions) => Promise<void> | void;
    initializeModuleOptions: PluginPackageInitializeModuleOptions;
    notifySuccess: () => void;
    notifyError: (error: unknown) => void;
}

@Options({
    name: 'commercial-web-public-app',
    components: {
        MhtkModuleInjector,
    }
})
export default class App extends Vue {

    /*----*\
    | Data |
    \*----*/

    // Composition
    protected teleportComponentList = setup(() => useTeleportComponentList());
    protected appStore = setup(() => useAppStore());
    protected developerSettingsStore = setup(() => useDeveloperSettingsStore());
    protected authenticationService = setup(() => useAuthenticationService());
    protected commwebService = setup(() => useCommwebService());
    protected ledsService = setup(() => useLedsService());
    protected nonsecureService = setup(() => useNonsecureService());
    protected appManagementService: AppManagementService;

    // Loading State
    protected loading = true;
    protected setupError: Error | null = null;

    // Webpack module initializers
    protected moduleInitializers: MhtkModuleInitialization[] = [];

    /*--------*\
    | Computed |
    \*--------*/

    protected get preloadCulpritCount() {
        return this.appController.preloadCulpritCount;
    }

    /*---------*\
    | Lifecycle |
    \*---------*/

    public created() {
        // Create app management service instance
        this.appManagementService = createAppManagementService({
            nonsecureService: this.nonsecureService
        });
    }

    public async mounted() {
        this.loading = true;
        try {
            await this.initializeApp();
        } catch (error) {
            /* istanbul ignore next */
            if (['development', 'production'].includes(process.env.NODE_ENV ?? '')) {
                // DO NOT USE LOGGER. This is useful information in production build, thus logger utility not used.
                console.error('[App] Initialize failed, error/code thrown: ', error);
            }
            this.setupError = error as Error;
        } finally {
            this.loading = false;
        }
    }

    /*-------*\
    | Methods |
    \*-------*/

    /**
     * Authenticates and makes all necessary API calls to load data before displaying the app.
     */
    public async initializeApp() {
        const {
            authenticationServiceMockAdapter,
            commwebServiceMockAdapter,
            ledsServiceMockAdapter,
            nonsecureServiceMockAdapter
        } = await this.setupDevelopmentMockData();

        try {
            let siteConfiguration: SiteConfiguration = (await this.appManagementService.getSiteConfiguration()).data;
            this.appStore.setSiteConfiguration(siteConfiguration);
        } catch (error) {
            console.warn('[App] Failed to retrieve site configuration.', error);
        }

        // Allow each federated module to run its own preload logic, if it provides it.
        let initializeModulePromises: Array<Promise<void>> = [];
        for (const packageName in remoteModuleImportMap) {
            try {
                let { getInitializeModule, initializeModule } = await remoteModuleImportMap[packageName]();
                // Use newer getInitializeModule API if available. It allows for the
                // Vue composition API to work inside of the initializeModule function.
                if (getInitializeModule) {
                    initializeModule = await getInitializeModule();
                }
                if (initializeModule) {
                    initializeModulePromises.push(new Promise<void>((resolve, reject) => {
                        const moduleInitializer = {
                            moduleName: packageName,
                            initializeModule: initializeModule as MhtkModuleInitialization['initializeModule'],
                            initializeModuleOptions: {
                                router: null as never,
                                store: null as never,
                                i18n: i18n as I18n,
                                authenticationService: null as never,
                                authenticationServiceMockAdapter: authenticationServiceMockAdapter,
                                appNotifications: null as never,
                                commwebService: null as never,
                                commwebServiceMockAdapter: commwebServiceMockAdapter,
                                ledsService: null as never,
                                ledsServiceMockAdapter: ledsServiceMockAdapter,
                                monitoringService: null as never,
                                nonsecureService: this.nonsecureService,
                                nonsecureServiceMockAdapter
                            },
                            notifySuccess: () => {
                                resolve();
                            },
                            notifyError: (error: unknown) => {
                                reject(error);
                            }
                        };
                        this.moduleInitializers.push(moduleInitializer);
                    }));
                }
            } catch (error) {
                // Ignore. If there's a problem with the module loading,
                // this will be caught later when the Vue Plugin does not load.
            }
        }
        await Promise.all(initializeModulePromises);

    }

    /**
     * Sets up service mock data when applicable.
     */
    /* istanbul ignore next */
    private async setupDevelopmentMockData() {
        let authenticationServiceMockAdapter: AxiosMockAdapter | undefined = undefined;
        let commwebServiceMockAdapter: AxiosMockAdapter | undefined = undefined;
        let ledsServiceMockAdapter: AxiosMockAdapter | undefined = undefined;
        let nonsecureServiceMockAdapter: AxiosMockAdapter | undefined = undefined;
        let serviceTestDataScenario = this.developerSettingsStore.serviceTestDataScenario;
        if (serviceTestDataScenario === null && window.location.host.split('.')[0].includes('author')) {
            serviceTestDataScenario = 'default';
        }
        if (serviceTestDataScenario !== null) {
            const { applyMocks } = await import(`@/mock/service/${
                serviceTestDataScenario
            }`);
            authenticationServiceMockAdapter = new AxiosMockAdapter(this.authenticationService.client);
            applyMocks(authenticationServiceMockAdapter);
            commwebServiceMockAdapter = new AxiosMockAdapter(this.commwebService.client);
            applyMocks(commwebServiceMockAdapter);
            ledsServiceMockAdapter = new AxiosMockAdapter(this.ledsService.client);
            applyMocks(ledsServiceMockAdapter);
            nonsecureServiceMockAdapter = new AxiosMockAdapter(this.nonsecureService.client);
            applyMocks(nonsecureServiceMockAdapter);
        }
        return {
            authenticationServiceMockAdapter,
            commwebServiceMockAdapter,
            ledsServiceMockAdapter,
            nonsecureServiceMockAdapter
        };
    }

    /**
     * Handle callbacks from the webpack module initialization step
     */
    onInitializeModuleSuccess(initializer: MhtkModuleInitialization) {
        initializer.notifySuccess();
    }
    onInitializeModuleError(initializer: MhtkModuleInitialization, error: unknown) {
        initializer.notifyError(error);
    }
}
